import { start } from '#~/control/calibration/commands/index.js';
import type { DeviceHandler } from '#~/device/index.js';
import { raiseError } from '#~/notifications/index.js';
import { Confirm } from './calibrate/index.js';
import { usePrompt } from '@studio/design-system/prompt';
import type { Device } from '@studio/models/device';
import { useLatestFn } from '@studio/react-utils/hooks';
import { createElement } from 'react';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';

/**
 * Hook that returns a function to imperatively calibrate a device.
 *
 * @returns Imperative function.
 */
export const useCalibrate = (): ((deviceId: Device['id'], callback?: DeviceHandler) => void) => {
  const prompt = usePrompt();
  const { track } = useAnalytics();

  return useLatestFn((deviceId: Device['id'], callback?: DeviceHandler) => {
    prompt.confirm({
      title: 'Camera Calibration Required',
      message:
        'This is a one-time process that will take a few minutes. To ensure a successful calibration, please review our list of best practices.',
      cancel: {
        label: 'Calibrate Later'
      },
      className: '!w-[525px]',
      content: createElement(Confirm),
      okay: {
        label: 'Start Calibration'
      },
      onConfirm: (answer: boolean) => {
        if (!answer) {
          return;
        }

        start(deviceId)
          .then(() => {
            // Dead-code removal (ENG-4026): the `track(SUCCESSFULLY_CALIBRATE_SOURCE)`
            // that used to live here was unreachable — all callers of
            // useCalibrate() omit `callback`, so this branch never ran the
            // emit. Calibration success is now reported by the canonical
            // `calibration_completed` event from the <Calibration> watchdog.
            // The callback path is preserved for any future caller that
            // passes one, just without the dead analytics call.
            if (!callback) {
              return;
            }

            queueMicrotask(() => {
              // eslint-disable-next-line promise/no-callback-in-promise
              callback(deviceId);
            });
          })
          .catch((error) => {
            // Migration-only dual-write — remove after the 7-day parity window
            // (PR 2 / ENG-4026). Canonical terminal event is calibration_completed.
            // Per rule: code-comments-required (ai.knowledge/global) — do NOT
            // delete this emit while the CANONICAL_INVENTORY Removal Gate is open.
            track(EVENT_NAMES.FAILED_CALIBRATION, EVENT_CATEGORIES.CALIBRATION);
            raiseError(error, { actionable: true });
          });
      }
    });
  });
};
